import React, { memo } from 'react';
import { Banner, MessageStatus } from '@nexus/lib-react/dist/core/Banner/Banner';
import { Paragraph2, Paragraph3 } from '@nexus/lib-react/dist/core/Typography';
import {
  Dac7Data,
  Dac7FormValidationResult,
  SellerBusinessDac7,
  SellerPrivateDac7,
} from '../../../../../typings/dac7';
import DAC7TabContentPrivate from './DAC7TabContentPrivate';
import DAC7TabContentBusiness from './DAC7TabContentBusiness';
import Dac7Layout from './components/Dac7Layout';
import Dac7CTABlock from './components/Dac7CTABlock';
import { useI18n } from '../../../../i18n';
import Dac7Input from './components/Dac7Input';

interface DAC7TabContentProps {
  dac7Data: NonNullable<Readonly<Dac7Data>>;
  validationResult: Dac7FormValidationResult;
  isDisabled: boolean;
  isDesktop: boolean;
  type: Dac7Data['type'];
}

const isDac7Private = (dac7Data: Readonly<Dac7Data>): dac7Data is SellerPrivateDac7 =>
  dac7Data.type === 'PRIVATE';
const isDac7Business = (dac7Data: Readonly<Dac7Data>): dac7Data is SellerBusinessDac7 =>
  dac7Data.type === 'BUSINESS';

interface TopBannerProps {
  validationResult: Dac7FormValidationResult;
  isDisabled: boolean;
}

const TopBanner = memo(({ validationResult }: TopBannerProps) => {
  const i18n = useI18n();

  // form data is invalid, we need to show the error banner
  if (validationResult.length > 0) {
    const ErrorMessages = validationResult.map(([, translation]) => (
      <Paragraph2>
        <li>{`${i18n(translation)}`}</li>
      </Paragraph2>
    ));
    const message = (
      <>
        <Paragraph3>{i18n`dlv_dac7_top_banner_error_bold_text`}</Paragraph3>
        <Paragraph2>{i18n`dlv_dac7_top_banner_error_description`}</Paragraph2>
        {ErrorMessages}
      </>
    );
    return <Banner mt={8} message={message} type={MessageStatus.ERROR} />;
  }

  const message = (
    <>
      <Paragraph3>{i18n`dlv_dac7_top_banner_warning_bold_text`}</Paragraph3>
      <Paragraph2>{i18n`dlv_dac7_top_banner_warning_description`}</Paragraph2>
    </>
  );
  return <Banner mt={8} mb={8} message={message} type={MessageStatus.WARNING} />;
});

const DAC7TabContent = ({ dac7Data, validationResult, isDesktop, type }: DAC7TabContentProps) => {
  const isDisabled = !!dac7Data.submittedAt;

  return (
    <Dac7Layout
      topBanner={<TopBanner validationResult={validationResult} isDisabled={isDisabled} />}
    >
      <Dac7Input type="hidden" name="dac7_type" value={type} />
      {isDac7Private(dac7Data) ? (
        <DAC7TabContentPrivate data={dac7Data} isDesktop={isDesktop} />
      ) : null}
      {isDac7Business(dac7Data) ? (
        <DAC7TabContentBusiness data={dac7Data} isDesktop={isDesktop} />
      ) : null}
      {isDisabled ? null : <Dac7CTABlock isDesktop={isDesktop} />}
    </Dac7Layout>
  );
};
export default memo(DAC7TabContent);
